import { useMemo, useRef, useState } from "react";
import { createCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import { ACADEMIC_CATALOGUE_CHANGED } from "./useAcademicCatalogue";
import type {
  AcademicReceipt,
  AcademicReview,
  CloneAcademicCurriculum,
} from "./generated/academic-catalogue";

export type AcademicCurriculumAction =
  "clone" | "submit" | "approve" | "request-changes" | "reject" | "publish";

type CurriculumActionRecord = Record<string, unknown> & {
  id: string;
  name: string;
  code: string;
  parentKind: "program" | "course";
  parentId: string;
  parentVersion: number;
  version: number;
  status: string;
};

const actionPermission: Record<AcademicCurriculumAction, string> = {
  clone: "institution.curriculum.manage",
  submit: "institution.curriculum.manage",
  approve: "institution.curriculum.approve",
  "request-changes": "institution.curriculum.approve",
  reject: "institution.curriculum.approve",
  publish: "institution.curriculum.publish",
};

const actionLabel: Record<AcademicCurriculumAction, string> = {
  clone: "Create revision draft",
  submit: "Submit for review",
  approve: "Approve exact version",
  "request-changes": "Request changes",
  reject: "Reject exact version",
  publish: "Publish exact version",
};

const actionStates: Record<AcademicCurriculumAction, string[]> = {
  clone: ["approved", "published", "changes_requested", "rejected"],
  submit: ["draft"],
  approve: ["in_review"],
  "request-changes": ["in_review"],
  reject: ["in_review"],
  publish: ["approved"],
};

function inferAction(route: string, record?: CurriculumActionRecord): AcademicCurriculumAction {
  if (route === "curriculum-maps") return "clone";
  if (record?.status === "draft") return "submit";
  if (record?.status === "in_review") return "approve";
  if (record?.status === "approved") return "publish";
  return "clone";
}

function isCurriculumRecord(record: Record<string, unknown>): record is CurriculumActionRecord {
  return (
    typeof record.id === "string" &&
    typeof record.name === "string" &&
    typeof record.code === "string" &&
    (record.parentKind === "program" || record.parentKind === "course") &&
    typeof record.parentId === "string" &&
    typeof record.parentVersion === "number" &&
    typeof record.version === "number" &&
    typeof record.status === "string"
  );
}

/** Approved fields in the original Institution curriculum action dialog. */
export function AcademicCurriculumFields({
  route,
  action: requestedAction,
  record,
  records,
  formId,
  onSaved,
  onBusy,
}: {
  route: string;
  action?: AcademicCurriculumAction;
  record?: Record<string, unknown>;
  records: Array<Record<string, unknown>>;
  formId: string;
  onSaved: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const identity = useIdentity();
  const currentRecord = record && isCurriculumRecord(record) ? record : undefined;
  const action = requestedAction || inferAction(route, currentRecord);
  const available = useMemo(
    () =>
      records.filter(isCurriculumRecord).filter(item => {
        if (!actionStates[action].includes(item.status)) return false;
        return action !== "publish" || !item.publicationId;
      }),
    [action, records],
  );
  const initialId =
    currentRecord && available.some(item => item.id === currentRecord.id)
      ? currentRecord.id
      : available[0]?.id || "";
  const [selectedId, setSelectedId] = useState(initialId);
  const [versionLabel, setVersionLabel] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const sender = useRef(createCommandSender({ headerOnly: true }));
  const submitting = useRef(false);
  const selected = available.find(item => item.id === selectedId);
  const permission = actionPermission[action];
  const canAct = !!identity.session?.permissions.includes(permission);
  const context = identity.session
    ? `${identity.session.tenantId}:${identity.session.user.id}:${identity.session.workspace.id}`
    : "";

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || !selected || !canAct) return;
    submitting.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const session = await checkIdentitySession();
      if (`${session.tenantId}:${session.user.id}:${session.workspace.id}` !== context)
        throw new Error("Your workspace changed. Reopen this curriculum action in its current Institution.");
      const path = `academic-catalogue/${selected.parentKind}/${encodeURIComponent(selected.parentId)}/curricula/${encodeURIComponent(selected.id)}/commands/${action}`;
      if (action === "clone") {
        const input: CloneAcademicCurriculum = {
          expectedVersion: selected.version,
          expectedParentVersion: selected.parentVersion,
          versionLabel: versionLabel.trim(),
          reason: reason.trim(),
        };
        await sender.current<AcademicReceipt>(path, input);
      } else {
        const input: AcademicReview = { expectedVersion: selected.version, reason: reason.trim() };
        await sender.current<AcademicReceipt>(path, input);
      }
      window.dispatchEvent(new Event(ACADEMIC_CATALOGUE_CHANGED));
      onSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The curriculum action could not be recorded.");
    } finally {
      submitting.current = false;
      setBusy(false);
      onBusy(false);
    }
  };

  return (
    <form id={formId} onSubmit={submit} className="form-fields-v2">
      {error && (
        <p role="alert" className="full">
          {error}
        </p>
      )}
      {!canAct && (
        <p role="alert" className="full">
          Current {permission} permission is required.
        </p>
      )}
      {!available.length && (
        <p role="alert" className="full">
          No exact curriculum version is currently eligible for {actionLabel[action].toLowerCase()}.
        </p>
      )}
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-version`}>Exact curriculum version</label>
        <select
          id={`${formId}-version`}
          required
          value={selectedId}
          onChange={event => setSelectedId(event.target.value)}
          disabled={busy || !available.length}
        >
          {!available.length && <option value="">No eligible version</option>}
          {available.map(item => (
            <option key={item.id} value={item.id}>
              {item.name} · {item.code} · {item.parentKind} · {item.status} · version {item.version}
            </option>
          ))}
        </select>
      </div>
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-label`}>
          {action === "clone" ? "New version label" : "Selected version label"}
        </label>
        <input
          id={`${formId}-label`}
          required={action === "clone"}
          minLength={action === "clone" ? 1 : undefined}
          maxLength={80}
          readOnly={action !== "clone"}
          value={action === "clone" ? versionLabel : selected?.code || ""}
          onChange={event => setVersionLabel(event.target.value)}
          placeholder={action === "clone" ? "Name the new draft version" : undefined}
          disabled={busy || !available.length}
        />
      </div>
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-reason`}>Reason for change</label>
        <textarea
          id={`${formId}-reason`}
          required
          minLength={10}
          maxLength={action === "clone" || action === "submit" ? 2000 : 1000}
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={busy || !available.length}
        />
      </div>
    </form>
  );
}

export const academicCurriculumActionLabel = actionLabel;
